import React, { useEffect, useRef } from "react";
import {
  Box,
  MenuItem,
  Select,
  InputLabel,
  SelectChangeEvent,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Button,
  Alert,
  IconButton,
} from "@mui/material";
import InfoOutlineIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import type { AppDispatch } from "@store/store";
import type { RootReducerType } from "@store/store";
import {
  getAuthorityListThunk,
  getCategoryListThunk,
  submitAmenitiesThunk,
} from "../redux/thunk/amenitiesThunk";
import {
  setAmenityField,
  resetAmenityState,
  setFirstSectionCompleted,
} from "../redux/amenitesSlice";
import { useSelector, useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { setActiveStep } from "../redux/stepperSlice";
import { StepsButton } from "./StepsButtons";
import { toast } from "react-toastify";
import {
  StyledFormControl,
  StyledTextField,
} from "@components/CustomInput/customInput";
import { useNavigate } from "react-router-dom";
import { getTypeOfPropertyThunk } from "../redux/thunk/sideRoadwaysThunk";
import { useGeolocation } from "@gis/components/useGeoLocation";
import AddIcon from "@mui/icons-material/Add";
import MapSearchInput from "@gis/components/MapSearchInput";
import AlertDialogBox from "./AlertDialogBox";
import ConfirmDialog from "@components/Modal/ConfirmModal";

// Define proper TypeScript interfaces
interface FieldOption {
  label: string;
  value: string | number;
}

interface FormField {
  type: string;
  label: string;
  name: string;
  options?: string[] | FieldOption[];
  optional?: boolean;
  inputType?: string;
}

interface Section {
  title: string;
  fields: FormField[];
}

const initialSections: Section[] = [
  {
    title: "Amenity Details",
    fields: [
      {
        type: "select",
        label: "Access Permission For",
        name: "category_id",
        options: [],
      },
      {
        type: "select",
        label: "Property Type",
        name: "property_type_id",
        options: [],
      },
      {
        type: "input",
        label: "Specify Property Type",
        name: "other_property_type",
        optional: true,
      },
    ],
  },
  {
    title: "Highway Amenity Location",
    fields: [
      {
        type: "input",
        label: "National Highway Number",
        name: "national_highway_number",
      },
      {
        type: "select",
        label: "Name of Authority to which National Highway is entrusted",
        name: "authority_id",
        options: [],
      },
      {
        type: "select",
        label: "Highway Category",
        name: "highway_category",
        options: ["2-Lane", "4-Lane", "6-Lane", "8-Lane"],
      },
      {
        type: "select",
        label: "Highway Side",
        name: "highway_side",
        options: ["Left-Hand Side", "Right-Hand Side"],
      },
    ],
  },
  {
    title: "Amenity Address",
    fields: [
      {
        type: "input",
        label: "Address Line 1",
        name: "address_1",
      },
      {
        type: "input",
        label: "Address Line 2",
        name: "address_2",
        optional: true,
      },
      {
        type: "input",
        label: "Khasra No. / Block No. / Survey No. / Gat No.",
        name: "khasra_number",
      },
      {
        type: "input",
        label: "Taluka",
        name: "taluka",
      },
      {
        type: "input",
        label: "Village / Town",
        name: "village",
      },
      {
        type: "input",
        label: "District",
        name: "district",
      },
      {
        type: "input",
        label: "Pincode",
        name: "pincode",
        inputType: "number",
      },
      {
        type: "input",
        label: "State / Union Territories",
        name: "state",
      },
    ],
  },
];

// Validation helper functions
const validateInput = {
  // Only alphabets and spaces
  alphabetsOnly: (value: string) => /^[a-zA-Z ]*$/.test(value),

  // Only numbers
  numbersOnly: (value: string) => /^[0-9]*$/.test(value),

  // Alphanumeric with any special characters for khasra number
  khasraNumber: (value: string) => /^[a-zA-Z0-9\s\W]*$/.test(value),

  // Alphanumeric with hyphen for highway number
  highwayNumber: (value: string) => /^[a-zA-Z0-9-]*$/.test(value),

  // Address fields: alphanumeric with specific special characters (. , / - #)
  address: (value: string) => /^[a-zA-Z0-9 .,/-]*$/.test(value),

  // Taluka: alphanumeric with spaces and commas
  taluka: (value: string) => /^[a-zA-Z0-9 ,]*$/.test(value),

  // Numeric fields (chainage) - prevent negative
  positiveNumber: (value: string) => !value.startsWith("-"),

  // Allow only digits and a single decimal point
  decimalNumber: (value: string) => /^\d*\.?\d*$/.test(value),
};

export const AmenitiesSection: React.FC<any> = ({ collapsed }) => {
  const {
    category_id,
    start_chainage_km,
    start_chainage_m,
    end_chainage_km,
    end_chainage_m,
    start_chainage,
    end_chainage,
    chainage,
    district,
    highway_category,
    khasra_number,
    taluka,
    national_highway_number,
    pincode,
    property_type_id,
    other_property_type,
    state,
    village,
    address_1,
    address_2,
    latitude,
    longitude,
    access_id,
    state_code,
    highway_side,
    authority_id,
    ro_id,
    piu_id,
  } = useSelector((state: RootReducerType) => state.amenities);

  const navigate = useNavigate();

  const { activeStep } = useSelector((state: RootReducerType) => state.stepper);
  const applicationEditId = useSelector(
    (state: RootReducerType) => state.applicationDashboard?.id,
  );
  const { gstNumber } = useSelector(
    (state: RootReducerType) => state.userRegistration,
  );

  const dispatch = useDispatch<AppDispatch>();

  const [sections, setSections] = React.useState<Section[]>(initialSections);
  const [expandedSection, setExpandedSection] = React.useState<number>(0);
  const [completedSections, setCompletedSections] = React.useState<number[]>(
    [],
  );

  const [showLocationSearch, setShowLocationSearch] =
    React.useState<boolean>(false);
  const [showMainSections, setShowMainSections] =
    React.useState<boolean>(false);
  const [hasSearchedLocation, setHasSearchedLocation] =
    React.useState<boolean>(false);

  const [openGSTAlert, setOpenGSTAlert] = React.useState<boolean>(false);
  const [openFuelStationDisclaimer, setOpenFuelStationDisclaimer] =
    React.useState(false);
  const { profile } = useSelector((state: RootReducerType) => state.profile);
  const organizations = profile?.organizations ?? [];
  const isInitialRender = React.useRef(true);

  // Reset form state when starting a new application (not editing)
  useEffect(() => {
    // If access_id is 0 or undefined AND applicationEditId is 0 or undefined, it's a new application
    if (!access_id && !applicationEditId) {
      dispatch(resetAmenityState());
    }
  }, []); // Run only once on mount
  useEffect(() => {
    const derivedStartChainage = start_chainage || chainage;
    const derivedEndChainage = end_chainage || chainage;

    if (derivedStartChainage) {
      const [km, m] = derivedStartChainage.split("+");

      if (km !== undefined && km !== null) {
        formik.setFieldValue("start_chainage_km", km || "");
      }
      if (m !== undefined && m !== null) {
        formik.setFieldValue("start_chainage_m", m || "");
      }
    }

    if (derivedEndChainage) {
      const [km, m] = derivedEndChainage.split("+");

      if (km !== undefined && km !== null) {
        formik.setFieldValue("end_chainage_km", km || "");
      }
      if (m !== undefined && m !== null) {
        formik.setFieldValue("end_chainage_m", m || "");
      }
    }
  }, []);

  useEffect(() => {
    if (showMainSections) {
      setExpandedSection(1);
    }
  }, [showMainSections]);

  // Get all fields for validation
  const allFields = React.useMemo(() => {
    return sections.flatMap((section) => section.fields);
  }, [sections]);

  // Fix: Convert 0 values to empty strings for form initialization
  const getFormInitialValue = (value: any) => {
    if (value === 0 || value === "0") {
      return "";
    }
    return value ?? "";
  };

  const formikInitialValues = {
    category_id: getFormInitialValue(category_id),
    property_type_id: getFormInitialValue(property_type_id),
    other_property_type: getFormInitialValue(other_property_type),
    national_highway_number: getFormInitialValue(national_highway_number),
    pincode: getFormInitialValue(pincode),
    address_1: getFormInitialValue(address_1),
    address_2: getFormInitialValue(address_2),
    state: getFormInitialValue(state),
    district: getFormInitialValue(district),
    village: getFormInitialValue(village),
    khasra_number: getFormInitialValue(khasra_number),
    taluka: getFormInitialValue(taluka),
    highway_category: getFormInitialValue(highway_category),
    highway_side: getFormInitialValue(highway_side),
    start_chainage_km: getFormInitialValue(
      start_chainage_km ??
        start_chainage?.split("+")[0] ??
        chainage?.split("+")[0] ??
        "",
    ),
    start_chainage_m: getFormInitialValue(
      start_chainage_m ??
        start_chainage?.split("+")[1] ??
        chainage?.split("+")[1] ??
        "",
    ),
    end_chainage_km: getFormInitialValue(
      end_chainage_km ?? end_chainage?.split("+")[0] ?? "",
    ),
    end_chainage_m: getFormInitialValue(
      end_chainage_m ?? end_chainage?.split("+")[1] ?? "",
    ),
    latitude: getFormInitialValue(latitude),
    longitude: getFormInitialValue(longitude),
    authority_id: getFormInitialValue(authority_id),
    ro_id: Number(ro_id),
    piu_id: Number(piu_id),
    piu_ids: [Number(piu_id)],
    // ro_id: 7,
    // piu_id: 82,
    // piu_ids: [82],
  };

  const formikValidationSchema = Yup.object({
    ...allFields.reduce(
      (acc, field) => {
        // Only add validation for non-optional fields
        if (!(field as FormField).optional) {
          acc[field.name] =
            field.type === "select"
              ? Yup.string().required(`${field.label} is required`)
              : Yup.string().required(`${field.label} is required`);
        }
        return acc;
      },
      {} as Record<string, any>,
    ),
    taluka: Yup.string()
      .matches(
        /^[a-zA-Z0-9 ,]+$/,
        "Only letters, numbers, spaces and commas are allowed",
      )
      .required("Taluka is required"),
    khasra_number: Yup.string()
      .trim()
      .min(1, "Khasra Number is required")
      .required("Khasra Number is required"),
    national_highway_number: Yup.string()
      .matches(
        /^[a-zA-Z0-9-]+$/,
        "Only letters, numbers and hyphen (-) are allowed",
      )
      .required("National Highway Number is required"),
    address_1: Yup.string()
      .matches(
        /^[a-zA-Z0-9 .,/-]+$/,
        "Only letters, numbers and . , / - are allowed",
      )
      .required("Address Line 1 is required"),
    address_2: Yup.string().matches(
      /^[a-zA-Z0-9 .,/-]*$/,
      "Only letters, numbers and . , / - are allowed",
    ),
    village: Yup.string()
      .matches(/^[a-zA-Z ]+$/, "Only alphabets and spaces are allowed")
      .required("Village / Town is required"),
    district: Yup.string()
      .matches(/^[a-zA-Z ]+$/, "Only alphabets and spaces are allowed")
      .required("District is required"),
    state: Yup.string()
      .matches(/^[a-zA-Z ]+$/, "Only alphabets and spaces are allowed")
      .required("State is required"),
    pincode: Yup.string()
      .matches(/^[0-9]{6}$/, "Pincode must be exactly 6 digits")
      .required("Pincode is required"),
    start_chainage_km: Yup.number()
      .typeError("Start Chainage Km must be a number")
      .required("Start Chainage Km is required")
      .min(0, "Start Chainage Km must be >= 0"),
    start_chainage_m: Yup.number()
      .typeError("Start Chainage m must be a number")
      .required("Start Chainage m is required")
      .min(0, "Start Chainage m must be >= 0")
      .max(9999, "Start Chainage m must be <= 9999"),
    end_chainage_km: Yup.number()
      .typeError("End Chainage Km must be a number")
      .required("End Chainage Km is required")
      .min(0, "End Chainage Km must be >= 0"),
    end_chainage_m: Yup.number()
      .typeError("End Chainage m must be a number")
      .required("End Chainage m is required")
      .min(0, "End Chainage m must be >= 0")
      .max(9999, "End Chainage m must be <= 9999"),
    latitude: Yup.number()
      .typeError("Latitude must be a number")
      .required("Latitude is required"),
    longitude: Yup.number()
      .typeError("Longitude must be a number")
      .required("Longitude is required"),
    other_property_type: Yup.string().when("property_type_id", {
      is: (value: any) => {
        const options =
          (sections[0]?.fields.find((f) => f.name === "property_type_id")
            ?.options as FieldOption[]) || [];

        const selected = options.find((option) => option.value == value);

        return selected?.label?.toLowerCase() === "others - please specify";
      },
      then: (schema) => schema.required("Please specify the property type"),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const formik = useFormik({
    initialValues: formikInitialValues,
    validationSchema: formikValidationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      // if (values.ro_id === 0) {
      //   toast.error(
      //     "Selected location at map in not under any juridiction of RO/PIU. Please select proper location.",
      //   );
      //   return;
      // }
      const {
        start_chainage_km,
        start_chainage_m,
        end_chainage_km,
        end_chainage_m,
        ...rest
      } = values;
      const latitude = values.latitude
        ? Number(Number(values.latitude).toFixed(6))
        : "";
      const longitude = values.longitude
        ? Number(Number(values.longitude).toFixed(6))
        : "";
      const submittedValues = {
        ...rest,
        ...(access_id !== 0 && { access_id: access_id }),
        pincode: Number(values.pincode) || "",
        khasra_number: values.khasra_number || "",
        taluka: values.taluka || "",
        gst_number: gstNumber || "",
        form_step: 1,
        start_chainage: `${values.start_chainage_km}+${values.start_chainage_m}`,
        end_chainage: `${values.end_chainage_km}+${values.end_chainage_m}`,
        latitude,
        longitude,
        state_code: state_code || "",
        other_property_type: values.other_property_type || "",
      };
      // return ;
      const response = await dispatch(submitAmenitiesThunk(submittedValues));
      if (response.payload.success) {
        dispatch(
          setAmenityField({
            name: "access_id",
            value: response.payload.data.id,
          }),
        );
        toast.success(response?.payload?.message);
        dispatch(setActiveStep(activeStep + 1));
      } else {
        toast.error(
          response?.payload?.response?.data?.message ||
            response?.payload?.message,
        );
      }
    },
  });

  // Check if first section is completed (category_id and property_type_id)
  const isFirstSectionCompleted = (): boolean => {
    return (
      formik.values.category_id !== "" &&
      formik.values.category_id !== 0 &&
      formik.values.property_type_id !== "" &&
      formik.values.property_type_id !== 0
    );
  };

  // Check if a section is completed
  const isSectionCompleted = (sectionIndex: number): boolean => {
    const section = sections[sectionIndex];
    if (!section) return false;

    return section.fields.every((field) => {
      if (field.optional) return true; // Optional fields don't block completion

      const value = formik.values[field.name as keyof typeof formik.values];
      // Fix: Check for empty string, null, undefined, and 0
      return (
        value !== "" &&
        value !== null &&
        value !== undefined &&
        value !== 0 &&
        value !== "0"
      );
    });
  };

  // Check if a section is enabled (previous sections are completed)
  const isSectionEnabled = (sectionIndex: number): boolean => {
    if (sectionIndex === 0) return true; // First section is always enabled

    // For other sections, check if all previous sections are completed
    for (let i = 0; i < sectionIndex; i++) {
      if (!completedSections.includes(i)) {
        return false;
      }
    }
    return true;
  };
  // Update completed sections when form values change
  React.useEffect(() => {
    const newCompletedSections: number[] = [];

    sections.forEach((_, index) => {
      if (isSectionCompleted(index)) {
        newCompletedSections.push(index);
      }
    });

    setCompletedSections(newCompletedSections);

    // Update global state when first section completion changes
    const isFirstComplete = newCompletedSections.includes(0);
    dispatch(setFirstSectionCompleted(isFirstComplete));

    // Only auto-expand if showing main sections and the user hasn't manually expanded a section
    if (showMainSections && !completedSections.includes(expandedSection)) {
      const firstIncompleteSection = sections.findIndex(
        (_, index) => !newCompletedSections.includes(index),
      );

      if (firstIncompleteSection !== -1) {
        setExpandedSection(firstIncompleteSection);
      } else {
        // All sections completed, expand the last one
        setExpandedSection(sections.length - 1);
      }
    }
  }, [formik.values, sections]);

  const fetchCategoryList = async () => {
    const categoryList = await dispatch(getCategoryListThunk(1));
    if (categoryList.payload.success) {
      setSections((prevSections) =>
        prevSections.map((section) => ({
          ...section,
          fields: section.fields.map((field) =>
            field.name === "category_id"
              ? {
                  ...field,
                  options: categoryList.payload.data.map((cat: any) => ({
                    label: cat.name,
                    value: cat.id,
                  })),
                }
              : field,
          ),
        })),
      );
    }
  };

  const fetchAuthorityListList = async () => {
    try {
      const authorityList = await dispatch(getAuthorityListThunk());
      if (authorityList?.payload?.success) {
        setSections((prevSections) =>
          prevSections.map((section) => ({
            ...section,
            fields: section.fields.map((field) =>
              field.name === "authority_id"
                ? {
                    ...field,
                    options: authorityList?.payload?.data?.map(
                      (authority: any) => ({
                        label: authority.name,
                        value: authority.id,
                      }),
                    ),
                  }
                : field,
            ),
          })),
        );
      }
    } catch (error) {
      console.error("Error fetching authority list:", error);
    }
  };

  React.useEffect(() => {
    const fetchTypeOfProperty = async () => {
      const categoryId = formik.values.category_id;

      if (!categoryId) return; // Only stop if empty

      if (categoryId == 1) {
        const org = organizations?.[0];
        if (!org) return;

        if (!org.gst_number) {
          setOpenGSTAlert(true);
          formik.setFieldValue("category_id", "");
          return;
        }
      }

      const res = await dispatch(
        getTypeOfPropertyThunk({ category_id: categoryId }),
      );

      if (res.payload?.success) {
        setSections((prevSections) =>
          prevSections.map((section) => ({
            ...section,
            fields: section.fields.map((field) =>
              field.name === "property_type_id"
                ? {
                    ...field,
                    options: res.payload.data.map((item: any) => ({
                      label: item.name,
                      value: item.id,
                    })),
                  }
                : field,
            ),
          })),
        );
      }
    };

    fetchTypeOfProperty();
  }, [formik.values.category_id, dispatch, category_id]);

  React.useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    if (
      formik.values.property_type_id &&
      !organizations?.[0]?.gst_number &&
      ![7, 8].includes(formik.values.property_type_id)
    ) {
      setOpenGSTAlert(true);

      formik.setFieldValue("property_type_id", "");

      return;
    }
  }, [formik.values.property_type_id]);

  React.useEffect(() => {
    fetchCategoryList();
    fetchAuthorityListList();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<any> | SelectChangeEvent<string>,
  ) => {
    const { name, value } = e.target as { name: string; value: string };

    const chainageFields = [
      "start_chainage_km",
      "start_chainage_m",
      "end_chainage_km",
      "end_chainage_m",
    ];

    // When Access Permission For changes, force user to reselect Property Type.
    if (name === "category_id") {
      formik.setFieldValue("property_type_id", "");
      dispatch(setAmenityField({ name: "property_type_id", value: 0 }));
    }

    // Validation rules for each field type

    // Chainage fields - allow only positive decimal numbers
    if (chainageFields.includes(name)) {
      if (!validateInput.positiveNumber(value)) {
        return;
      }
      if (!validateInput.decimalNumber(value)) {
        return;
      }
    }

    // Pincode - only numbers (6 digits)
    if (name === "pincode" && value && !validateInput.numbersOnly(value)) {
      return;
    }

    // National highway number - alphanumeric with hyphen
    if (
      name === "national_highway_number" &&
      !validateInput.highwayNumber(value)
    ) {
      return;
    }

    // Khasra number - numbers and forward slash
    if (name === "khasra_number" && !validateInput.khasraNumber(value)) {
      return;
    }

    // Taluka - alphanumeric with spaces
    if (name === "taluka" && !validateInput.taluka(value)) {
      return;
    }

    // Village, District, State - only alphabets and spaces
    if (
      ["village", "district", "state"].includes(name) &&
      !validateInput.alphabetsOnly(value)
    ) {
      return;
    }

    // Address fields - allow alphanumeric and specific special characters
    if (
      ["address_1", "address_2"].includes(name) &&
      !validateInput.address(value)
    ) {
      return;
    }

    formik.handleChange(e);

    if (name === "property_type_id") {
      setOpenFuelStationDisclaimer(String(value) === "1");
    }

    // Fix: Convert empty string to 0 for numeric fields that need to be stored as numbers
    let finalValue: string | number = value;
    if (
      (name === "category_id" ||
        name === "property_type_id" ||
        name === "authority_id") &&
      value === ""
    ) {
      finalValue = 0;
    }

    dispatch(setAmenityField({ name, value: finalValue }));
  };

  const handleAccordionChange =
    (sectionIndex: number) =>
    (event: React.SyntheticEvent, isExpanded: boolean) => {
      if (isExpanded && isSectionEnabled(sectionIndex)) {
        setExpandedSection(sectionIndex);
      }
    };

  const showSelectedSections = () => {
    if (
      latitude ||
      longitude ||
      start_chainage_km ||
      start_chainage_m ||
      end_chainage_km ||
      end_chainage_m
    ) {
      setShowMainSections(true);
      setShowLocationSearch(false);
      setHasSearchedLocation(true); // Mark as searched to prevent showing search section again
      setExpandedSection(0); // Expand first main section
    }
  };

  const handleBackToFirstSection = () => {
    setShowLocationSearch(false);
    setShowMainSections(false);
    setExpandedSection(0);
    isSectionCompleted(0);
    showSelectedSections();
  };

  const handleSearchLocation = () => {
    setShowMainSections(true);
    setShowLocationSearch(false);
    setHasSearchedLocation(true); // Mark that user has searched
    setExpandedSection(0); // Expand first main section
  };

  useEffect(() => {
    // If we already have location data, skip the search section
    showSelectedSections();
  }, [latitude, longitude]);

  // Helper function to safely render error messages
  const renderError = (error: any): string => {
    if (typeof error === "string") return error;
    if (Array.isArray(error)) return error.join(", ");
    return "Invalid value";
  };

  const renderLocationSearchSection = () => {
    return (
      <Accordion
        key="location-search"
        expanded={true}
        sx={{
          mb: 2,
          "&:before": { display: "none" },
          boxShadow: "none",
          borderRadius: "8px !important",
          // overflow: "hidden",
          transition: "all 0.3s ease",
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: "#f5f5f5",
            borderBottom: "1px solid #e0e0e0",
            minHeight: "48px !important",
            "& .MuiAccordionSummary-content": {
              margin: "12px 0",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            },
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontSize: "16px", fontWeight: 600 }}
            display="flex"
            flexDirection="column"
          >
            <Typography component="span" sx={{ fontWeight: "bold" }}>
              <IconButton
                onClick={handleBackToFirstSection}
                sx={{ py: 0, my: 0 }}
              >
                <ArrowBackIcon />
              </IconButton>{" "}
              Search Your Amenity Location
            </Typography>
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: "16px 24px", overflow: "visible" }}>
          <Box
            className="amenities-wrapper flex flex-col justify-center items-center"
            sx={{ overflow: "visible" }}
          >
            <Box
              key="location_search"
              sx={{ width: "100%", position: "relative", zIndex: 1000 }}
            >
              <MapSearchInput />
              <Typography variant="h6" sx={{ mt: 1, color: "red" }}>
                Please search & click on highlighted highway stretch to locate
                your Ammenity/ Utility.
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mt: 2,
                  gap: 1,
                }}
              >
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBackToFirstSection}
                  variant="outlined"
                  sx={{ flex: 1 }}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  sx={{ flex: 1 }}
                  onClick={handleSearchLocation}
                >
                  Search
                </Button>
              </Box>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderField = (field: FormField) => {
    const renderChainageSection = () => (
      <Box>
        <Box
          display="flex"
          alignItems="flex-start"
          sx={{ width: { xs: "100%", sm: "100%", md: "27rem" }, mb: 1 }}
        >
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            mr={2}
            position="relative"
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: "#fbbf24",
                position: "relative",
                top: "6px",
                border: "1px solid #000000",
              }}
            />

            <Box
              sx={{
                width: "2px",
                height: { xs: "175px", sm: "175px", md: "170px" },
                background:
                  "repeating-linear-gradient(transparent, transparent 4px, #ccc 4px, #ccc 8px)",
                marginY: "4px",
              }}
            />

            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: "#22c55e",
                position: "relative",
                bottom: "6px",
                border: "1px solid #000000",
              }}
            />
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography fontWeight={600} fontSize="14px" mb={1}>
              Enter Start Chainage (KM / M)
              <span style={{ color: "red" }}>*</span>
            </Typography>
            <Box display="flex" gap={1} mb={0.5} alignItems="center">
              <Box sx={{ width: { xs: "100%", sm: "45%" } }}>
                <InputLabel>Km</InputLabel>
                <StyledTextField
                  name="start_chainage_km"
                  value={formik.values.start_chainage_km}
                  onChange={handleChange}
                  onBlur={formik.handleBlur}
                  inputProps={{
                    inputMode: "decimal",
                    pattern: "[0-9]*[.]?[0-9]*",
                  }}
                  sx={{ width: "100%" }}
                />
              </Box>
              <span style={{ marginTop: "24px" }}>+</span>
              <Box sx={{ width: { xs: "100%", sm: "45%" } }}>
                <InputLabel>m</InputLabel>
                <StyledTextField
                  name="start_chainage_m"
                  value={formik.values.start_chainage_m}
                  onChange={handleChange}
                  onBlur={formik.handleBlur}
                  inputProps={{
                    inputMode: "decimal",
                    pattern: "[0-9]*[.]?[0-9]*",
                  }}
                  sx={{ width: "100%" }}
                />
              </Box>
            </Box>

            {(formik.touched.start_chainage_km ||
              formik.touched.start_chainage_m) &&
              (formik.errors.start_chainage_km ||
                formik.errors.start_chainage_m) && (
                <Box my={0.5} sx={{ width: "100%" }}>
                  <span
                    style={{
                      color: "red",
                      fontSize: 12,
                      fontWeight: 500,
                    }}
                  >
                    {renderError(
                      formik.errors.start_chainage_km ||
                        formik.errors.start_chainage_m,
                    )}
                  </span>
                </Box>
              )}

            <Typography fontWeight={600} fontSize="14px" mt={2.5} mb={1}>
              Enter End Chainage (KM / M)
              <span style={{ color: "red" }}>*</span>
            </Typography>
            <Box display="flex" gap={1} mb={0.5} alignItems="center">
              <Box sx={{ width: { xs: "100%", sm: "45%" } }}>
                <InputLabel>Km</InputLabel>
                <StyledTextField
                  name="end_chainage_km"
                  value={formik.values.end_chainage_km}
                  onChange={handleChange}
                  onBlur={formik.handleBlur}
                  inputProps={{
                    inputMode: "decimal",
                    pattern: "[0-9]*[.]?[0-9]*",
                  }}
                  sx={{ width: "100%" }}
                />
              </Box>
              <span style={{ marginTop: "24px" }}>+</span>
              <Box sx={{ width: { xs: "100%", sm: "45%" } }}>
                <InputLabel>m</InputLabel>
                <StyledTextField
                  name="end_chainage_m"
                  value={formik.values.end_chainage_m}
                  onChange={handleChange}
                  onBlur={formik.handleBlur}
                  inputProps={{
                    inputMode: "decimal",
                    pattern: "[0-9]*[.]?[0-9]*",
                  }}
                  sx={{ width: "100%" }}
                />
              </Box>
            </Box>

            {(formik.touched.end_chainage_km ||
              formik.touched.end_chainage_m) &&
              (formik.errors.end_chainage_km ||
                formik.errors.end_chainage_m) && (
                <Box my={0.5} sx={{ width: "100%" }}>
                  <span
                    style={{
                      color: "red",
                      fontSize: 12,
                      fontWeight: 500,
                    }}
                  >
                    {renderError(
                      formik.errors.end_chainage_km ||
                        formik.errors.end_chainage_m,
                    )}
                  </span>
                </Box>
              )}
          </Box>
        </Box>
        <Box sx={{ display: "flex", mb: 2 }}>
          <Typography
            sx={{
              color: "#CC8801",
              fontSize: "14px",
              fontWeight: 600,
              mr: 0.5,
            }}
          >
            *
          </Typography>
          <Typography
            sx={{
              color: "#CC8801",
              fontSize: "14px",
              fontWeight: 600,
            }}
          >
            Please Pin Chainage location on the map to continue
          </Typography>
        </Box>
      </Box>
    );

    const fieldNode = (
      <Box
        key={field.name}
        sx={{ width: { xs: "100%", sm: "100%", md: "27rem" } }}
      >
        <InputLabel
          htmlFor={field.name}
          sx={{
            textWrap: "wrap",
            mb: 1,
            ...(field.name === "authority_id" && { ml: 0.8 }),
          }}
        >
          {field.label}{" "}
          {!field.optional && <span style={{ color: "red" }}>*</span>}
        </InputLabel>
        {field.type === "select" ? (
          <StyledFormControl
            sx={{ width: "100%", mb: 1.8 }}
            variant="outlined"
            fullWidth
            error={Boolean(
              formik.touched[field.name as keyof typeof formik.touched] &&
              formik.errors[field.name as keyof typeof formik.errors],
            )}
          >
            <Select
              id={field.name}
              name={field.name}
              value={String(
                formik.values[field.name as keyof typeof formik.values],
              )}
              onChange={handleChange}
              onBlur={formik.handleBlur}
              IconComponent={ExpandMoreIcon}
              // disabled={
              //   field.name === "authority_id" &&
              //   Boolean(formik.values.authority_id)
              // }
              displayEmpty
              MenuProps={{
                PaperProps: {
                  sx: {
                    width: { xs: "100%", sm: "100%", md: "27rem" },
                    maxWidth: "100%",
                  },
                },
              }}
            >
              <MenuItem value="" disabled>
                Select {field.label}
              </MenuItem>

              {field.options?.map((option: any) =>
                typeof option === "object" ? (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={{ whiteSpace: "normal", lineHeight: 1.5 }}
                  >
                    {option.label}
                  </MenuItem>
                ) : (
                  <MenuItem
                    key={option}
                    value={option}
                    sx={{ whiteSpace: "normal", lineHeight: 1.5 }}
                  >
                    {option}
                  </MenuItem>
                ),
              )}
            </Select>
          </StyledFormControl>
        ) : (
          <StyledTextField
            id={field.name}
            inputProps={{
              min: 0,
              step: "any",
              inputMode: "numeric",
              onWheel: (e) => (e.target as HTMLInputElement).blur(),
              onKeyDown: (e) => {
                if (["ArrowUp", "ArrowDown"].includes(e.key)) {
                  e.preventDefault();
                }
              },
            }}
            sx={{
              width: "100%",
              mb: 1.8,
              "& input[type=number]": {
                MozAppearance: "textfield",
              },
              "& input[type=number]::-webkit-outer-spin-button": {
                WebkitAppearance: "none",
                margin: 0,
              },
              "& input[type=number]::-webkit-inner-spin-button": {
                WebkitAppearance: "none",
                margin: 0,
              },
            }}
            name={field.name}
            type={field.inputType || "text"}
            value={
              formik.values[field.name as keyof typeof formik.values] ?? ""
            }
            onChange={handleChange}
            onBlur={formik.handleBlur}
            fullWidth
            error={Boolean(
              formik.touched[field.name as keyof typeof formik.touched] &&
              formik.errors[field.name as keyof typeof formik.errors],
            )}
            helperText={
              formik.touched[field.name as keyof typeof formik.touched] &&
              formik.errors[field.name as keyof typeof formik.errors] ? (
                <span
                  style={{
                    color: "red",
                    fontSize: 11,
                    fontWeight: 500,
                    marginTop: 2,
                  }}
                >
                  {renderError(
                    formik.errors[field.name as keyof typeof formik.errors],
                  )}
                </span>
              ) : undefined
            }
          />
        )}
        {formik.touched[field.name as keyof typeof formik.touched] &&
          formik.errors[field.name as keyof typeof formik.errors] &&
          field.type === "select" && (
            <div
              style={{
                color: "red",
                fontSize: 11,
                fontWeight: 500,
                marginTop: 2,
              }}
            >
              {renderError(
                formik.errors[field.name as keyof typeof formik.errors],
              )}
            </div>
          )}
      </Box>
    );

    // Special handling for national_highway_number field to include chainage details
    if (field.name === "national_highway_number") {
      return (
        <React.Fragment key={field.name}>
          {fieldNode}
          {renderChainageSection()}
        </React.Fragment>
      );
    }
    return fieldNode;
  };

  const renderSection = (section: Section, index: number) => {
    const isCompleted = completedSections.includes(index);
    const isEnabled = isSectionEnabled(index);

    const isExpanded = expandedSection === index;

    return (
      <>
        <Accordion
          key={section.title}
          expanded={isExpanded}
          onChange={handleAccordionChange(index)}
          disabled={!isEnabled}
          className="Accordion-1"
          sx={{
            "&:before": { display: "none" },
            boxShadow: "none",
            transition: "all 0.3s ease",
            "&.Mui-expanded": {
              margin: "0 0 0 0",
            },
          }}
        >
          <AccordionSummary
            expandIcon={isEnabled ? <ExpandMoreIcon /> : null}
            className="AccordionSummary-1"
            sx={{
              backgroundColor: "#FFF",
              borderBottom: "1px solid #e0e0e0",
              borderTop: "1px solid #e0e0e0",
              minHeight: "48px !important",
              "& .MuiAccordionSummary-content": {
                backgroundColor: "#FFF",
                margin: "0 !important",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              },
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontSize: "16px", fontWeight: 600 }}
              display="flex"
              flexDirection="column"
            >
              <Typography component="span" sx={{ fontWeight: "bold" }}>
                {section.title}
              </Typography>
              {!isEnabled && (
                <Typography variant="caption">
                  (Complete previous sections first)
                </Typography>
              )}
            </Typography>
            {isCompleted && (
              <CheckCircleIcon sx={{ color: "success.main", ml: 1 }} />
            )}
          </AccordionSummary>
          <AccordionDetails
            sx={{
              padding: { xs: "12px 4px", sm: "16px 12px", md: "16px 24px" },
            }}
            className="AccordionDetails-1"
          >
            <Box
              className="amenities-wrapper flex flex-col justify-center items-center"
              sx={{
                width: "100%",
                px: { xs: 0, sm: 2, md: 4 },
                alignItems: "center",
              }}
            >
              {section.fields
                .filter((field) => {
                  if (field.name !== "other_property_type") return true;

                  const propertyOptions = (
                    section.fields.find((f) => f.name === "property_type_id")
                      ?.options ?? []
                  ).filter(
                    (option): option is FieldOption =>
                      typeof option !== "string",
                  );

                  const selectedProperty = propertyOptions.find(
                    (option) => option.value == formik.values.property_type_id,
                  );

                  return (
                    selectedProperty?.label?.toLowerCase() ===
                    "others - please specify"
                  );

                  return (
                    String(selectedProperty?.label).toLowerCase() ===
                    "others - please specify"
                  );
                })
                .map((field) => renderField(field))}
            </Box>
          </AccordionDetails>
        </Accordion>
        {index === 0 && expandedSection === 0 && (
          <Box sx={{ textAlign: "center", pb: 2 }}>
            <Button
              sx={{
                background: "#3a1c73",
                padding: "8px 16px",
                color: "#fff",
                marginTop: "-0.5rem",
                width: { xs: "100%", sm: "100%", md: "30rem" },
                "&.Mui-disabled": {
                  backgroundColor: "#9E9E9E",
                  color: "#fff",
                },
              }}
              onClick={() => setShowLocationSearch(true)}
              disabled={!isCompleted}
            >
              Search and Select on GIS Map
            </Button>
            <br />
            {!showMainSections && !showLocationSearch && (
              <>
                <Button
                  disabled
                  sx={{
                    width: { xs: "100%", sm: "100%", md: "30rem" },
                    background: "#FFF",
                    padding: "8px 16px",
                    color: "#3a1c73",
                    marginTop: "8px",
                    border: "1px solid #3a1c73",
                  }}
                >
                  <AddIcon sx={{ mr: 1 }} />
                  Highway Amenity Location
                </Button>
                <br />
                <Button
                  disabled
                  sx={{
                    width: { xs: "100%", sm: "100%", md: "30rem" },
                    background: "#FFF",
                    padding: "8px 16px",
                    color: "#3a1c73",
                    marginTop: "8px",
                    border: "1px solid #3a1c73",
                  }}
                >
                  <AddIcon sx={{ mr: 1 }} />
                  Highway Alignment Details
                </Button>
              </>
            )}
          </Box>
        )}
      </>
    );
  };

  const renderFirstSectionOnly = () => (
    <Box>{renderSection(sections[0], 0)}</Box>
  );

  const renderMainSections = () => (
    <Box>{sections.map((section, index) => renderSection(section, index))}</Box>
  );

  const backButtonClickHandler = () => {
    navigate("/dashboard");
  };

  return (
    <>
      <Box
        sx={{
          position: { xs: "static", md: "absolute" },
          zIndex: 20,
          top: { md: 8 },
          left: { md: 35 },
          width: { xs: "100%", sm: "100%", md: "394px" },
          maxWidth: "100vw",
          height: "fit-content",
          overflow: "visible",
          px: { xs: 1, sm: 2, md: 0 },
          className: "amenities-container",
        }}
      >
        <Box
          className="form-wrapper"
          sx={{
            maxHeight: { xs: "none", md: "29.5rem" },
            backgroundColor: "#fff",
            overflow: showLocationSearch ? "visible" : "hidden",
            overflowY: showLocationSearch ? "visible" : "auto",
            padding: { xs: "0px", sm: "0px", md: "0px !important" },
            transition: "overflow 0.3s ease",
          }}
        >
          <form onSubmit={formik.handleSubmit}>
            <Box>
              {!showLocationSearch &&
                !showMainSections &&
                renderFirstSectionOnly()}
              {showLocationSearch && renderLocationSearchSection()}
              {showMainSections && !showLocationSearch && renderMainSections()}
            </Box>
          </form>
        </Box>
      </Box>
      <Box
        className="stepper-btns gray:br-t-500 mt-3"
        sx={{
          position: { xs: "static", md: "absolute" },
          width: "100%",
          left: { md: "0px" },
          bottom: { md: "0px" },
          zIndex: 10,
          px: { xs: 1, sm: 2, md: 0 },
        }}
      >
        <StepsButton
          onBack={() => backButtonClickHandler()}
          onNext={formik.handleSubmit}
          nextDisabled={completedSections.length !== 3}
        />
      </Box>
      <AlertDialogBox
        open={openGSTAlert}
        onClose={() => setOpenGSTAlert(false)}
      />

      <ConfirmDialog
        open={openFuelStationDisclaimer}
        title="Important Information"
        message={
          <>
            <strong>Disclaimer:</strong>
            <br />
            <Typography>
              This application can be submitted only by the authorized signing
              authority of the concerned Oil Company.
            </Typography>
          </>
        }
        confirmText="I Understand"
        onConfirm={() => setOpenFuelStationDisclaimer(false)}
        onClose={() => setOpenFuelStationDisclaimer(false)}
      />
    </>
  );
};

export default AmenitiesSection;
